import { Div, Input, IRouteEvent, router, emitter } from '@codesuma/baseline'
import { Page } from '../../components/page'
import { BackIcon } from 'components/back-icon'
import { ListCircle } from '../../components/list-circle'
import publicListsService, { PublicListEntry } from '../../services/public-lists'
import { matchWords } from '../../utils/match-words'
import style from './index.module.css'

// Opening a circle grows it into the public list page, as on home, so Page's
// slide sits out both halves of that: the way there and the way back.
const morph = ({ to, from, isBack }: IRouteEvent) =>
    !!to?.includes('/l/') || (!!isBack && from.includes('/l/'))

// Lowercase, with punctuation as spaces, so "watch" finds "Must-Watch".
const plain = (s: string) => ' ' + s.toLowerCase().replace(/[^a-z0-9À-ɏ]+/g, ' ')

// What the search box matches a list by: its title, description and category.
const wordsOf = (l: PublicListEntry) => plain(`${l.title} ${l.description} ${l.category}`)

// Discover: the curated public-list catalog. Tap a circle to preview it at
// /l/:slug, where "Save a copy" creates a private copy (services/public-lists).
export const DiscoverPage = () => {
    // Page() gives the shared slide transition (enter from bottom, exit up;
    // reversed on back). Override its background with the themed page tokens.
    const base = Page(morph)
    base.style({ background: 'var(--page-bg)', color: 'var(--page-fg)' })

    const header = Div()
    header.addClass(style.header)
    const back = BackIcon('currentColor')
    const title = Div('Discover')
    title.addClass(style.title)
    header.append(back, title)

    const intro = Div('Ready-made lists to start from. Saving one gives you your own private copy.')
    intro.addClass(style.intro)

    // Filters the circles as you type, the way the icon picker's search does.
    const search = Input('Search, e.g. movies or camping', 'text', { autocomplete: 'off' })
    search.addClass(style.search)
    search.attr('enterkeyhint', 'search')
    search.attr('autocorrect', 'off')
    search.on('input', () => filter())
    // The circles are already filtered, so return only closes the keyboard.
    search.on('enter', () => search.blur())

    const empty = Div('No list for that yet. Try another word.')
    empty.addClass(style.status)
    empty.style({ display: 'none' })

    // Everything under the header scrolls, the intro included. load() rebuilds
    // only `content`, so the intro survives it (baseline's empty() is permanent).
    const content = Div()
    const scroller = Div()
    scroller.addClass(style.scroller)
    scroller.append(intro, search, empty, content)

    base.append(header, scroller)

    let hasCards = false

    // Each category's title and grid, with its circles and the words they match.
    type Group = { title: ReturnType<typeof Div>; grid: ReturnType<typeof Div>; members: { circle: ReturnType<typeof ListCircle>; words: string }[] }
    let groups: Group[] = []

    // Hides the circles the search doesn't match, and a category whose circles
    // are all hidden. Inline display, so it wins over the circle's own class.
    const filter = () => {
        const query = plain(search.value())
        let shown = 0
        for (const { title, grid, members } of groups) {
            let left = 0
            for (const { circle, words } of members) {
                const show = matchWords(words, query)
                circle.style({ display: show ? '' : 'none' })
                if (show) left++
            }
            title.style({ display: left ? '' : 'none' })
            grid.style({ display: left ? '' : 'none' })
            shown += left
        }
        empty.style({ display: groups.length && !shown ? '' : 'none' })
    }

    // Status rows are built fresh every time — baseline's empty() strips a
    // reused component's children permanently.
    const renderStatus = (msg: string, retry?: () => void) => {
        content.empty()
        hasCards = false
        groups = []
        filter()
        const s = Div(msg)
        s.addClass(style.status)
        content.append(s)
        if (retry) {
            const r = Div('Try again')
            r.addClass(style.retry)
            r.el.onclick = retry
            content.append(r)
        }
    }

    // The same circle as the user's own lists, colored by slug — the hue the
    // saved copy keeps. Filled in place by load().
    const circles: ReturnType<typeof ListCircle>[] = []
    const circle = (entry: PublicListEntry) => {
        const c = ListCircle({ id: entry.slug, name: entry.title, created_by: '', created_at: '', role: 'owner' })
        c.on('circle-open', () => router.goto('/l/' + entry.slug, { name: entry.title }))
        return c
    }
    emitter.on('theme', () => circles.forEach(c => c.recolor()))

    // One title per category; it leaves upward with the intro during the open morph.
    const titles: ReturnType<typeof Div>[] = []

    const load = async () => {
        if (!hasCards) renderStatus('Loading…')
        try {
            const lists = await publicListsService.getCatalog()
            content.empty()
            hasCards = false
            if (!lists.length) return renderStatus('Nothing here yet — check back soon.')
            circles.splice(0, circles.length, ...lists.map(circle))
            // Categories in the order the catalog first uses them, so a new
            // category in the database needs no change here.
            const categories = lists.map(l => l.category).filter((c, i, all) => all.indexOf(c) === i)
            titles.length = 0
            groups = []
            for (const category of categories) {
                const title = Div(category)
                title.addClass(style.category)
                const grid = Div()
                grid.addClass(style.grid)
                const members = circles.map((circle, i) => ({ circle, words: wordsOf(lists[i]) }))
                    .filter((_, i) => lists[i].category === category)
                grid.append(...members.map(m => m.circle))
                titles.push(title)
                groups.push({ title, grid, members })
                content.append(title, grid)
            }
            hasCards = true
            filter()
        } catch (err) {
            console.warn('Failed to load public lists:', err)
            // Keep already-shown cards on a failed refresh; otherwise say so.
            if (!hasCards) renderStatus("Couldn't load lists — are you offline?", load)
        }
    }

    base.on('enter', (e: IRouteEvent) => {
        // Undo a morph exit's fade (a no-op after a slide).
        base.style({ opacity: '', pointerEvents: '' })
        header.style({ transform: '', opacity: '' }, 200)
        intro.style({ transform: '', opacity: '' }, 200)
        search.style({ transform: '', opacity: '' }, 200)
        titles.forEach(t => t.style({ transform: '', opacity: '' }, 200))
        // Back from a public list: bring the circles back into place, as home
        // does, and keep the search. Anything else starts afresh and reloads.
        if (morph(e)) circles.forEach(c => c.contract())
        else {
            search.clear()
            filter()
            load()
        }
    })

    // The morph itself: the tapped circle grows to fill the screen and the rest
    // scatter (list-circle expand), then the page fades out under the list.
    base.on('exit', ({ to }: IRouteEvent) => {
        if (!to?.includes('/l/')) return
        const slug = to.split('/').pop() as string
        const target = circles.find(c => c.item.id === slug)
        if (target) {
            const rect = target.el.getBoundingClientRect()
            circles.forEach(c => c.expand(rect.left + rect.width / 2, rect.top + rect.height / 2, slug))
        }
        // The header, intro, search and category titles leave upward, as home's
        // header does. The keyboard closes with them.
        search.blur()
        header.style({ transform: 'translateY(-120%)', opacity: '0' })
        intro.style({ transform: 'translateY(-120%)', opacity: '0' })
        search.style({ transform: 'translateY(-120%)', opacity: '0' })
        titles.forEach(t => t.style({ transform: 'translateY(-120%)', opacity: '0' }))
        base.style({ opacity: '0', pointerEvents: 'none' }, 520)
    })

    return base
}
